import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ShieldAlert, RefreshCw, Trash2, PlusCircle, MessageSquarePlus, ChevronDown, ChevronUp, Send, Clock, Eye, Wrench, CheckCircle2, XCircle, Archive, Download, Square, Radio, MessageSquareText, MinusCircle } from 'lucide-react';
import { API_CONFIG, STATUS_API_URL } from '../config/api.config';
import { prepareCommonHeaders } from '../services/baseApi';
import { Dialog } from '../components/common/Dialog';
import type { SupportTicket, TicketStatus } from '../services/supportTicketApi';
import SensorRoutesTab from '../components/superadmin/SensorRoutesTab';

const SUPER_ADMIN_EMAILS = ['adam@thermal-systems.co.uk', 'dave@thermal-systems.co.uk'];

type CustomerRow = { id: number; name: string; mqtt_topic?: string };

type SensorCounts = {
  total: number;
  companies: { id: number; name: string; schema: string; sensorCount: number | null }[];
};

function deriveSchemaName(companyName: string): string {
  return String(companyName).toLowerCase().replace(/[^a-z0-9_]/g, '_');
}

const EMPTY_CREATE = { companyName: '', adminEmail: '', adminPassword: '', adminFirstName: '', adminLastName: '' };

// ── ticket helpers ────────────────────────────────────────────────────────────

const STATUS_META: Record<TicketStatus, { label: string; color: string; icon: React.ReactNode }> = {
  open:         { label: 'Open',         color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',   icon: <Clock className="h-3 w-3" /> },
  under_review: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300', icon: <Eye className="h-3 w-3" /> },
  in_progress:  { label: 'In Progress',  color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300', icon: <Wrench className="h-3 w-3" /> },
  completed:    { label: 'Completed',    color: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',  icon: <CheckCircle2 className="h-3 w-3" /> },
  wont_fix:     { label: "Won't Fix",    color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',        icon: <XCircle className="h-3 w-3" /> },
};

const TYPE_LABELS: Record<string, string> = {
  bug: '🐛 Bug', feature_request: '✨ Feature', other: '💬 Other',
};

function StatusBadge({ status }: { status: TicketStatus }) {
  const m = STATUS_META[status] ?? STATUS_META.open;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${m.color}`}>
      {m.icon}{m.label}
    </span>
  );
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function TicketRow({ ticket, onRefresh, onDelete }: { ticket: SupportTicket; onRefresh: () => void; onDelete: (id: number) => void }) {
  const [open, setOpen]     = useState(false);
  const [reply, setReply]   = useState('');
  const [status, setStatus] = useState<TicketStatus | ''>('');
  const [sending, setSending]   = useState(false);
  const [deleting, setDeleting] = useState(false);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await fetch(`${API_CONFIG.baseURL}/super-admin/tickets/${ticket.id}/updates`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...prepareCommonHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: reply.trim(), ...(status ? { new_status: status } : {}) }),
      });
      setReply('');
      setStatus('');
      onRefresh();
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ticket #${ticket.id}: "${ticket.title}"? This cannot be undone.`)) return;
    onDelete(ticket.id);
    setDeleting(true);
    try {
      const res = await fetch(`${API_CONFIG.baseURL}/super-admin/tickets/${ticket.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: prepareCommonHeaders(),
      });
      if (!res.ok) onRefresh();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        className="w-full text-left px-4 py-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-start gap-3"
        onClick={() => setOpen(v => !v)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <span className="text-xs text-gray-400">#{ticket.id}</span>
            <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{ticket.company_name}</span>
            <span className="text-xs text-gray-500">{TYPE_LABELS[ticket.type] ?? ticket.type}</span>
            <StatusBadge status={ticket.status} />
          </div>
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{ticket.title}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {ticket.user_name} · {timeAgo(ticket.created_at)}
            {ticket.updates.length > 0 && ` · ${ticket.updates.length} update${ticket.updates.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />}
      </button>
      <button
        onClick={handleDelete}
        disabled={deleting}
        className="px-3 py-3 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-40 border-l border-gray-200 dark:border-gray-700 flex-shrink-0"
        title="Delete ticket"
      >
        <Trash2 className="h-4 w-4" />
      </button>

      {open && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-900/30 space-y-3">
          {/* original */}
          <div className="bg-white dark:bg-gray-800 rounded p-3">
            <p className="text-xs text-gray-500 mb-1">{ticket.user_name} &lt;{ticket.user_email}&gt; · {new Date(ticket.created_at).toLocaleString('en-GB')}</p>
            <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{ticket.description}</p>
          </div>
          {/* thread */}
          {ticket.updates.map(u => (
            <div key={u.id} className={`rounded p-3 ${u.is_admin ? 'bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800' : 'bg-white dark:bg-gray-800'}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{u.author_name}</span>
                {u.is_admin && <span className="text-[10px] px-1.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 rounded-full">Support</span>}
                <span className="text-xs text-gray-400">{timeAgo(u.created_at)}</span>
                {u.new_status && <span className="ml-auto"><StatusBadge status={u.new_status} /></span>}
              </div>
              <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{u.message}</p>
            </div>
          ))}
          {/* reply */}
          <div className="space-y-2">
            <textarea
              rows={3}
              value={reply}
              onChange={e => setReply(e.target.value)}
              placeholder="Reply as TS1 Support…"
              className="w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={status}
                onChange={e => setStatus(e.target.value as TicketStatus | '')}
                className="text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">Keep current status</option>
                {(Object.keys(STATUS_META) as TicketStatus[]).map(s => (
                  <option key={s} value={s}>{STATUS_META[s].label}</option>
                ))}
              </select>
              <button
                onClick={send}
                disabled={sending || !reply.trim()}
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
              >
                <Send className="h-3.5 w-3.5" />
                {sending ? 'Sending…' : 'Send Reply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── archiving tab ────────────────────────────────────────────────────────────

type ArchiveCompany = {
  id: number;
  name: string;
  schema: string;
  bytesUsed: number | null;
  capBytes: number | null;
  overCap: boolean;
  lastCheckedAt: string | null;
};

type ArchiveRow = {
  id: number;
  storage_path: string;
  range_start: string;
  range_end: string;
  row_count: number;
  file_size_bytes: number;
  triggered_by: string;
  created_at: string;
};

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toFixed(1)} ${units[i]}`;
}

function ArchivingTab() {
  const [companies, setCompanies] = useState<ArchiveCompany[] | null>(null);
  const [selectedSchema, setSelectedSchema] = useState('');
  const [cutoffDate, setCutoffDate] = useState('');
  const [chunkDays, setChunkDays] = useState('1');
  const [dryRun, setDryRun] = useState(true);
  const [confirmText, setConfirmText] = useState('');
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [archives, setArchives] = useState<ArchiveRow[] | null>(null);
  const esRef = useRef<EventSource | null>(null);

  const loadCompanies = useCallback(async () => {
    const res = await fetch(`${API_CONFIG.baseURL}/super-admin/archive/companies`, {
      method: 'GET', credentials: 'include', headers: prepareCommonHeaders(),
    });
    if (res.ok) setCompanies(await res.json());
  }, []);

  const loadArchives = useCallback(async (schema: string) => {
    if (!schema) { setArchives(null); return; }
    const res = await fetch(`${API_CONFIG.baseURL}/super-admin/archive/archives?schema=${encodeURIComponent(schema)}`, {
      method: 'GET', credentials: 'include', headers: prepareCommonHeaders(),
    });
    if (res.ok) setArchives(await res.json());
  }, []);

  useEffect(() => { void loadCompanies(); }, [loadCompanies]);
  useEffect(() => { void loadArchives(selectedSchema); }, [selectedSchema, loadArchives]);

  const appendLog = (line: string) => setLog(l => [...l, line]);

  const selectedCompany = companies?.find(c => c.schema === selectedSchema) ?? null;
  const needsConfirm = !dryRun;
  const confirmed = !needsConfirm || confirmText.trim() === 'ARCHIVE';
  const canRun = !!selectedSchema && !!cutoffDate && confirmed && !running;

  const runArchive = () => {
    if (!canRun) return;
    setLog([]);
    setRunning(true);
    const params = new URLSearchParams({ schema: selectedSchema, cutoffDate, chunkDays, dryRun: String(dryRun) });
    const es = new EventSource(`${API_CONFIG.baseURL}/super-admin/archive/run?${params.toString()}`, { withCredentials: true });
    esRef.current = es;

    es.onmessage = (e) => {
      const ev = JSON.parse(e.data);
      switch (ev.type) {
        case 'config':
          appendLog(`Starting ${ev.dryRun ? 'DRY RUN' : 'LIVE'} archive for ${ev.schema} — everything older than ${String(ev.cutoffDate).slice(0, 10)}, ${ev.chunkDays}-day chunks`);
          break;
        case 'start':
          appendLog(`=== ${ev.schema} ===`);
          break;
        case 'chunk':
          appendLog(`  ${String(ev.chunkStart).slice(0, 10)}: ${ev.rows} rows, ${formatBytes(ev.bytes)}${ev.dryRun ? ' (dry run, not written)' : ''}`);
          break;
        case 'error':
          appendLog(`  ERROR: ${ev.message}`);
          break;
        case 'cancelled':
          appendLog('  --- stopped early ---');
          break;
        case 'summary':
          appendLog(`  --- summary --- chunks archived=${ev.chunksArchived} rows=${ev.totalRows}${ev.dryRun ? ' (dry run)' : ''}`);
          break;
        case 'fatal':
          appendLog(`FATAL: ${ev.message}`);
          break;
        case 'done':
          appendLog('Run complete.');
          es.close();
          esRef.current = null;
          setRunning(false);
          void loadCompanies();
          void loadArchives(selectedSchema);
          break;
      }
    };
    es.onerror = () => {
      appendLog('[connection closed]');
      es.close();
      esRef.current = null;
      setRunning(false);
    };
  };

  const stopArchive = () => {
    esRef.current?.close();
    esRef.current = null;
    appendLog('[stopped by user]');
    setRunning(false);
  };

  const downloadArchive = async (id: number) => {
    const res = await fetch(`${API_CONFIG.baseURL}/super-admin/archive/download-url/${id}`, {
      method: 'GET', credentials: 'include', headers: prepareCommonHeaders(),
    });
    if (!res.ok) return;
    const { url } = await res.json();
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Exports old <code className="text-xs bg-gray-200 dark:bg-gray-700 px-1 rounded">sensor_data</code> rows to Supabase Storage via ArchiveService, verifies the upload, then deletes them from Postgres.
      </p>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Company database usage</h3>
          <button type="button" onClick={() => void loadCompanies()}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline">
            <RefreshCw className="h-3 w-3" />Refresh
          </button>
        </div>
        {companies === null && <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>}
        {companies && companies.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-app-panel dark:bg-gray-800 shadow">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  {['Company', 'Schema', 'Used', 'Allowance', 'Status', 'Last checked', ''].map(h => (
                    <th key={h} className={`px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase ${h ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {companies.map(c => (
                  <tr key={c.schema} className={`hover:bg-gray-50 dark:hover:bg-gray-900/30 ${c.schema === selectedSchema ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''}`}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{c.name}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-600 dark:text-gray-300">{c.schema}</td>
                    <td className="px-4 py-3 text-sm tabular-nums text-gray-900 dark:text-white">{formatBytes(c.bytesUsed)}</td>
                    <td className="px-4 py-3 text-sm tabular-nums text-gray-600 dark:text-gray-300">{formatBytes(c.capBytes)}</td>
                    <td className="px-4 py-3">
                      {c.overCap
                        ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Over cap</span>
                        : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">OK</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">{c.lastCheckedAt ? new Date(c.lastCheckedAt).toLocaleString() : 'never'}</td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" onClick={() => { setSelectedSchema(c.schema); setConfirmText(''); }}
                        className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 border border-indigo-200 dark:border-indigo-800">
                        Select
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-app-panel dark:bg-gray-800 rounded-lg shadow border-2 border-indigo-500 dark:border-indigo-400 p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company</label>
            <select
              value={selectedSchema}
              onChange={e => { setSelectedSchema(e.target.value); setConfirmText(''); }}
              disabled={running}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
            >
              <option value="">Select a company…</option>
              {(companies ?? []).map(c => (
                <option key={c.schema} value={c.schema}>{c.name} ({c.schema})</option>
              ))}
            </select>
            {selectedCompany && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {formatBytes(selectedCompany.bytesUsed)} used of {formatBytes(selectedCompany.capBytes)} allowance
                {selectedCompany.overCap && <span className="text-amber-700 dark:text-amber-400 font-medium"> — over cap</span>}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Archive everything older than</label>
            <input type="date" value={cutoffDate} onChange={e => setCutoffDate(e.target.value)} disabled={running}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chunk size (days)</label>
            <input type="number" min={1} value={chunkDays} onChange={e => setChunkDays(e.target.value)} disabled={running}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white" />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={dryRun} onChange={e => { setDryRun(e.target.checked); setConfirmText(''); }} disabled={running} />
          Dry run (recommended — logs what would happen, writes nothing)
        </label>

        {!dryRun && (
          <div className="p-3 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-800 dark:text-amber-300">
            This will upload files to Supabase Storage and permanently delete the archived rows from Postgres.
            Type <b>ARCHIVE</b> to enable the run button.
            <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)} disabled={running}
              placeholder="Type ARCHIVE to confirm"
              className="w-full mt-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white" />
          </div>
        )}

        <div className="flex items-center gap-2">
          <button type="button" onClick={runArchive} disabled={!canRun}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            <Archive className="h-4 w-4" />Run
          </button>
          <button type="button" onClick={stopArchive} disabled={!running}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
            <Square className="h-4 w-4" />Stop
          </button>
        </div>

        {log.length > 0 && (
          <pre className="bg-gray-900 text-gray-200 text-xs font-mono rounded-lg p-3 h-56 overflow-y-auto whitespace-pre-wrap">{log.join('\n')}</pre>
        )}
      </div>

      {selectedSchema && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Existing archives for {selectedSchema}</h3>
          {archives === null && <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>}
          {archives && archives.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No archives yet.</p>}
          {archives && archives.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-app-panel dark:bg-gray-800 shadow">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    {['Range', 'Rows', 'Size', 'Triggered by', 'Created', ''].map(h => (
                      <th key={h} className={`px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase ${h ? 'text-left' : 'text-right'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {archives.map(a => (
                    <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{a.range_start.slice(0, 10)} – {a.range_end.slice(0, 10)}</td>
                      <td className="px-4 py-3 text-sm tabular-nums text-gray-900 dark:text-white">{a.row_count}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{formatBytes(a.file_size_bytes)}</td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-600 dark:text-gray-300">{a.triggered_by}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{a.created_at.slice(0, 10)}</td>
                      <td className="px-4 py-3 text-right">
                        <button type="button" onClick={() => void downloadArchive(a.id)}
                          className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 border border-indigo-200 dark:border-indigo-800">
                          <Download className="h-4 w-4" />Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── SMS billing tab ──────────────────────────────────────────────────────────

type SmsUsage = {
  months: string[]; // "YYYY-MM", newest first, current month included
  companies: { id: number; name: string; schema: string; sms: Record<string, number>; email: Record<string, number> }[];
};

function formatMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

function SmsBillingTab() {
  const [usage, setUsage] = useState<SmsUsage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [rate, setRate] = useState(() => localStorage.getItem('ts1SmsUnitRate') ?? '');
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_CONFIG.baseURL}/super-admin/sms-usage`, {
        method: 'GET', credentials: 'include', headers: prepareCommonHeaders(),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); setError(j.error || `HTTP ${res.status}`); return; }
      const data: SmsUsage = await res.json();
      setUsage(data);
      // Default to the last complete calendar month — that's the one you invoice
      setSelectedMonth(prev => prev || data.months[1] || data.months[0] || '');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const rateNum = parseFloat(rate);
  const hasRate = Number.isFinite(rateNum) && rateNum > 0;
  const prevMonth = usage ? usage.months[usage.months.indexOf(selectedMonth) + 1] : undefined;

  const rows = (usage?.companies ?? [])
    .map(c => ({
      ...c,
      selected: c.sms[selectedMonth] ?? 0,
      previous: prevMonth ? (c.sms[prevMonth] ?? 0) : 0,
      emailSelected: c.email[selectedMonth] ?? 0,
      total12m: Object.values(c.sms).reduce((s, n) => s + n, 0),
    }))
    .sort((a, b) => b.selected - a.selected);

  const totalSelected = rows.reduce((s, r) => s + r.selected, 0);

  const saveRate = (v: string) => {
    setRate(v);
    localStorage.setItem('ts1SmsUnitRate', v);
  };

  const downloadCsv = () => {
    if (!usage) return;
    const header = hasRate ? 'Company,SMS sent,Rate (GBP),Amount (GBP)' : 'Company,SMS sent';
    const lines = rows
      .filter(r => r.selected > 0)
      .map(r => {
        const name = `"${r.name.replace(/"/g, '""')}"`;
        return hasRate
          ? `${name},${r.selected},${rateNum.toFixed(4)},${(r.selected * rateNum).toFixed(2)}`
          : `${name},${r.selected}`;
      });
    const blob = new Blob([[header, ...lines].join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sms-usage-${selectedMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        SMS alert volume per customer from each schema's <code className="text-xs bg-gray-200 dark:bg-gray-700 px-1 rounded">external_alarm_log</code> — one credit per recipient per dispatched alert. Pick the billing month, set your unit rate, and export the CSV for invoicing in Xero.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Billing month</label>
          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white">
            {(usage?.months ?? []).map((m, i) => (
              <option key={m} value={m}>{formatMonth(m)}{i === 0 ? ' (in progress)' : ''}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Unit rate (£ per SMS)</label>
          <input type="number" min="0" step="0.01" value={rate} onChange={e => saveRate(e.target.value)} placeholder="e.g. 0.10"
            className="w-36 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={downloadCsv} disabled={!usage || totalSelected === 0}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
            <Download className="h-4 w-4" />Export CSV
          </button>
          <button type="button" onClick={() => void load()} disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-app-panel dark:bg-gray-800 shadow px-5 py-4 flex items-center gap-4">
        <div className="rounded-lg bg-indigo-100 dark:bg-indigo-900/40 p-2.5">
          <MessageSquareText className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <p className="text-2xl font-bold tabular-nums text-gray-900 dark:text-white">
            {usage === null ? '—' : totalSelected.toLocaleString()}
            {hasRate && usage !== null && (
              <span className="ml-3 text-base font-semibold text-green-700 dark:text-green-400">≈ £{(totalSelected * rateNum).toFixed(2)}</span>
            )}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Total SMS sent in {selectedMonth ? formatMonth(selectedMonth) : '…'} across all customers</p>
        </div>
      </div>

      {error && <div className="p-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 text-sm">{error}</div>}
      {usage === null && !error && <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>}

      {usage && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-app-panel dark:bg-gray-800 shadow">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                {['Company', 'SMS', prevMonth ? `Prev (${prevMonth})` : 'Prev', 'Email', '12-mo SMS', hasRate ? 'Amount' : ''].filter(h => h !== '').map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map(r => (
                <tr key={r.id} className={`hover:bg-gray-50 dark:hover:bg-gray-900/30 ${r.selected === 0 ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{r.name}</td>
                  <td className="px-4 py-3 text-sm tabular-nums font-semibold text-gray-900 dark:text-white">{r.selected.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-gray-600 dark:text-gray-300">{r.previous.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-gray-600 dark:text-gray-300">{r.emailSelected.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-gray-600 dark:text-gray-300">{r.total12m.toLocaleString()}</td>
                  {hasRate && (
                    <td className="px-4 py-3 text-sm tabular-nums font-medium text-green-700 dark:text-green-400">
                      {r.selected > 0 ? `£${(r.selected * rateNum).toFixed(2)}` : '—'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {usage && (
        <div>
          <button type="button" onClick={() => setShowHistory(v => !v)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline">
            {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            12-month history
          </button>
          {showHistory && (
            <div className="mt-2 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-app-panel dark:bg-gray-800 shadow">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Company</th>
                    {usage.months.map(m => (
                      <th key={m} className="px-3 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400">{m}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {rows.filter(r => r.total12m > 0).map(r => (
                    <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">{r.name}</td>
                      {usage.months.map(m => (
                        <td key={m} className="px-3 py-3 text-sm tabular-nums text-right text-gray-600 dark:text-gray-300">
                          {(r.sms[m] ?? 0).toLocaleString()}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

interface StatusEntry {
  name: string;
  ok?: boolean;
  skipped?: boolean;
  detail?: string;
  checkedAt?: string | null;
  changedAt?: string | null;
}

function timeAgoShort(iso?: string | null) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

function StatusRowIcon({ entry }: { entry: StatusEntry }) {
  if (entry.skipped) return <MinusCircle className="h-4 w-4 text-gray-400" />;
  return entry.ok
    ? <CheckCircle2 className="h-4 w-4 text-green-500" />
    : <XCircle className="h-4 w-4 text-red-500" />;
}

function ServiceStatusTab() {
  const [entries, setEntries] = useState<StatusEntry[] | null>(null);
  const [overall, setOverall] = useState<string | null>(null);
  const [lastRunAt, setLastRunAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${STATUS_API_URL}/status/full`);
      if (!res.ok) { setError(`StatusAPI returned HTTP ${res.status}`); return; }
      const data = await res.json();
      setEntries(data.services ?? []);
      setOverall(data.overall ?? null);
      setLastRunAt(data.lastRunAt ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reach StatusAPI');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {overall && (
            <span className={`font-semibold ${overall === 'operational' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {overall === 'operational' ? 'All systems operational' : 'Degraded'}
            </span>
          )}
          {lastRunAt && <span className="ml-2">· last checked {timeAgoShort(lastRunAt)}</span>}
        </p>
        <button type="button" onClick={() => void load()} disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-3 mb-4 text-sm text-red-700 dark:text-red-300">
          {error} — StatusAPI itself may be down or {STATUS_API_URL} is unreachable from your browser.
        </div>
      )}

      {entries && (
        <div className="bg-app-panel dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700 overflow-hidden">
          {entries.map((e) => (
            <div key={e.name} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <StatusRowIcon entry={e} />
                <span className="font-medium text-gray-900 dark:text-white">{e.name}</span>
              </div>
              <div className="flex-1 text-right text-gray-500 dark:text-gray-400 truncate px-2">
                {e.skipped ? 'Not configured' : (e.detail ?? (e.ok ? 'OK' : 'No detail'))}
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">{timeAgoShort(e.checkedAt)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SuperAdminPage() {
  const [rows, setRows] = useState<CustomerRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CustomerRow | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createFields, setCreateFields] = useState(EMPTY_CREATE);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // ── sensor counts state ──
  const [sensorCounts, setSensorCounts] = useState<SensorCounts | null>(null);

  // ── tickets state ──
  const [tickets, setTickets] = useState<SupportTicket[] | null>(null);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketFilter, setTicketFilter] = useState<TicketStatus | 'all'>('all');

  // ── tab state ──
  type SuperAdminTab = 'tickets' | 'customers' | 'billing' | 'archiving' | 'status' | 'routes';
  const [activeTab, setActiveTab] = useState<SuperAdminTab>('tickets');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_CONFIG.baseURL}/super-admin/customers`, {
        method: 'GET', credentials: 'include', headers: prepareCommonHeaders(),
      });
      if (res.status === 401) { setError('Not signed in.'); setRows([]); return; }
      if (res.status === 403) { setError('Permission denied — you do not have super admin access.'); setRows([]); return; }
      if (!res.ok) { const j = await res.json().catch(() => ({})); setError(j.error || `HTTP ${res.status}`); setRows([]); return; }
      setRows(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed'); setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSensorCounts = useCallback(async () => {
    try {
      const res = await fetch(`${API_CONFIG.baseURL}/super-admin/sensor-counts`, {
        method: 'GET', credentials: 'include', headers: prepareCommonHeaders(),
      });
      if (res.ok) setSensorCounts(await res.json());
    } catch {
      // non-critical stat — leave as null on failure
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setTicketsLoading(true);
    try {
      const res = await fetch(`${API_CONFIG.baseURL}/super-admin/tickets`, {
        method: 'GET', credentials: 'include', headers: prepareCommonHeaders(),
      });
      if (res.status === 403) { setTickets([]); return; }
      if (!res.ok) return;
      setTickets(await res.json());
    } finally {
      setTicketsLoading(false);
    }
  }, []);

  useEffect(() => { void load(); void loadTickets(); void loadSensorCounts(); }, []);

  const closeDeleteDialog = () => { if (deleteSubmitting) return; setDeleteTarget(null); setDeleteConfirmName(''); setDeleteError(null); };
  const confirmDeleteEnabled = deleteTarget !== null && deleteConfirmName.trim() === deleteTarget.name.trim() && !deleteSubmitting;

  const submitDelete = async () => {
    if (!deleteTarget || !confirmDeleteEnabled) return;
    setDeleteSubmitting(true); setDeleteError(null);
    try {
      const res = await fetch(`${API_CONFIG.baseURL}/super-admin/customers/${deleteTarget.id}`, {
        method: 'DELETE', credentials: 'include', headers: prepareCommonHeaders(),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) { setDeleteError(typeof j.error === 'string' ? j.error : `HTTP ${res.status}`); return; }
      closeDeleteDialog(); await load();
    } catch (e) { setDeleteError(e instanceof Error ? e.message : 'Request failed'); }
    finally { setDeleteSubmitting(false); }
  };

  const closeCreateDialog = () => { if (createSubmitting) return; setCreateOpen(false); setCreateFields(EMPTY_CREATE); setCreateError(null); };

  const submitCreate = async () => {
    setCreateSubmitting(true); setCreateError(null);
    try {
      const res = await fetch(`${API_CONFIG.baseURL}/super-admin/customers`, {
        method: 'POST', credentials: 'include',
        headers: { ...prepareCommonHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(createFields),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) { setCreateError(typeof j.error === 'string' ? j.error : `HTTP ${res.status}`); return; }
      closeCreateDialog(); await load();
    } catch (e) { setCreateError(e instanceof Error ? e.message : 'Request failed'); }
    finally { setCreateSubmitting(false); }
  };

  const createValid = createFields.companyName.trim() !== '' && createFields.adminEmail.trim() !== '' && createFields.adminPassword.trim() !== '' && !createSubmitting;

  const displayedTickets = ticketFilter === 'all' ? (tickets ?? []) : (tickets ?? []).filter(t => t.status === ticketFilter);
  const openCount = (tickets ?? []).filter(t => ['open','under_review','in_progress'].includes(t.status)).length;

  return (
    <div className="min-h-screen bg-app-canvas dark:bg-gray-900 p-4 sm:p-8">
      <div className="max-w-5xl mx-auto">

        {/* ── Shared header ── */}
        <div className="flex items-center gap-2 mb-6">
          <ShieldAlert className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Super Admin</h1>
        </div>

        {/* ── Tab bar ── */}
        <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
          {(['tickets', 'customers', 'billing', 'archiving', 'status', 'routes'] as SuperAdminTab[]).map(tab => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab === 'tickets' &&
                <>Support Tickets{openCount > 0 && <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">{openCount}</span>}</>}
              {tab === 'customers' && 'Customers'}
              {tab === 'billing' && 'SMS Billing'}
              {tab === 'archiving' && 'Archiving'}
              {tab === 'status' && 'Service Status'}
              {tab === 'routes' && 'Sensor Routes'}
            </button>
          ))}
        </div>

        {/* ── Support Tickets tab ── */}
        {activeTab === 'tickets' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {tickets === null ? 'Loading…' : `${openCount} active · ${tickets.length} total across all companies`}
              </p>
              <button type="button" onClick={() => void loadTickets()} disabled={ticketsLoading}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                <RefreshCw className={`h-4 w-4 ${ticketsLoading ? 'animate-spin' : ''}`} />Refresh
              </button>
            </div>

            {tickets && tickets.length > 0 && (
              <div className="mb-3 flex gap-1.5 flex-wrap">
                {(['all', 'open', 'under_review', 'in_progress', 'completed', 'wont_fix'] as const).map(s => (
                  <button key={s} onClick={() => setTicketFilter(s)}
                    className={`px-3 py-1 text-xs rounded-full font-medium border transition-colors ${
                      ticketFilter === s
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-indigo-400'
                    }`}>
                    {s === 'all' ? `All (${tickets.length})` : STATUS_META[s].label}
                  </button>
                ))}
              </div>
            )}

            {ticketsLoading && tickets === null && <p className="text-gray-500 dark:text-gray-400 text-sm py-4">Loading tickets…</p>}
            {tickets !== null && displayedTickets.length === 0 && (
              <div className="text-center py-10 text-gray-400 dark:text-gray-500">
                <MessageSquarePlus className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">{ticketFilter === 'all' ? 'No tickets yet.' : `No ${STATUS_META[ticketFilter as TicketStatus]?.label ?? ''} tickets.`}</p>
              </div>
            )}
            <div className="space-y-2">
              {displayedTickets.map(t => (
                <TicketRow key={t.id} ticket={t} onRefresh={loadTickets} onDelete={id => setTickets(prev => prev?.filter(t => t.id !== id) ?? [])} />
              ))}
            </div>
          </div>
        )}

        {/* ── Customers tab ── */}
        {activeTab === 'customers' && (
          <div>
            <div className="mb-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-app-panel dark:bg-gray-800 shadow px-5 py-4 flex items-center gap-4">
              <div className="rounded-lg bg-indigo-100 dark:bg-indigo-900/40 p-2.5">
                <Radio className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="text-2xl font-bold tabular-nums text-gray-900 dark:text-white">
                  {sensorCounts === null ? '—' : sensorCounts.total.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total sensors across all sites</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Customers in <code className="text-xs bg-gray-200 dark:bg-gray-700 px-1 rounded">metadb.companies</code>
              </p>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => { setCreateOpen(true); setCreateFields(EMPTY_CREATE); setCreateError(null); }}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700">
                  <PlusCircle className="h-4 w-4" />New Customer
                </button>
                <button type="button" onClick={() => { void load(); void loadSensorCounts(); }} disabled={loading}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh
                </button>
              </div>
            </div>

            {error && <div className="mb-4 p-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 text-sm">{error}</div>}
            {loading && !rows && <p className="text-gray-600 dark:text-gray-400">Loading…</p>}
            {rows && rows.length === 0 && !error && <p className="text-gray-600 dark:text-gray-400">No companies returned.</p>}
            {rows && rows.length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-app-panel dark:bg-gray-800 shadow">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      {['ID','Name','Schema','MQTT topic','Sensors',''].map(h => (
                        <th key={h} className={`px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase ${h ? 'text-left' : 'text-right'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map(r => (
                      <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                        <td className="px-4 py-3 text-sm tabular-nums text-gray-900 dark:text-white">{r.id}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{r.name}</td>
                        <td className="px-4 py-3 text-xs font-mono text-gray-600 dark:text-gray-300">{deriveSchemaName(r.name)}</td>
                        <td className="px-4 py-3 text-xs font-mono text-gray-600 dark:text-gray-300">{r.mqtt_topic ?? '—'}</td>
                        <td className="px-4 py-3 text-sm tabular-nums text-gray-900 dark:text-white">
                          {sensorCounts?.companies.find(c => c.id === r.id)?.sensorCount ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button type="button" onClick={() => { setDeleteTarget(r); setDeleteConfirmName(''); setDeleteError(null); }}
                            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 border border-red-200 dark:border-red-800">
                            <Trash2 className="h-4 w-4" />Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 max-w-prose">
              Schema derived from company name (lowercase, non-alphanumeric → underscore). Deleting runs <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">DROP SCHEMA … CASCADE</code>.
            </p>
          </div>
        )}

        {/* ── SMS Billing tab ── */}
        {activeTab === 'billing' && <SmsBillingTab />}

        {/* ── Archiving tab ── */}
        {activeTab === 'archiving' && <ArchivingTab />}

        {/* ── Service Status tab ── */}
        {activeTab === 'status' && <ServiceStatusTab />}

        {/* ── Sensor Routes tab ── */}
        {activeTab === 'routes' && <SensorRoutesTab />}

      </div>

      {/* ── Dialogs ── */}
      <Dialog isOpen={createOpen} onClose={closeCreateDialog} panelClassName="max-w-lg">
        <div className="pr-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create new customer</h2>
          <div className="space-y-3">
            {[
              { key: 'companyName', label: 'Company name *', type: 'text', placeholder: 'Acme Corp' },
              { key: 'adminEmail', label: 'Admin email *', type: 'email', placeholder: 'admin@acme.com' },
              { key: 'adminPassword', label: 'Admin password *', type: 'password', placeholder: '••••••••' },
              { key: 'adminFirstName', label: 'First name', type: 'text', placeholder: 'Jane' },
              { key: 'adminLastName', label: 'Last name', type: 'text', placeholder: 'Smith' },
            ].map(({ key, label, type, placeholder }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
                <input type={type} value={createFields[key as keyof typeof createFields]}
                  onChange={e => setCreateFields(f => ({ ...f, [key]: e.target.value }))}
                  placeholder={placeholder} disabled={createSubmitting} autoComplete="off"
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white" />
              </div>
            ))}
            {createFields.companyName.trim() && (
              <p className="text-xs text-gray-500">Schema: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{deriveSchemaName(createFields.companyName)}</code></p>
            )}
          </div>
          {createError && <p className="text-sm text-red-600 dark:text-red-400 mt-3">{createError}</p>}
          <div className="flex flex-wrap justify-end gap-2 mt-4">
            <button type="button" onClick={closeCreateDialog} disabled={createSubmitting}
              className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">Cancel</button>
            <button type="button" onClick={() => void submitCreate()} disabled={!createValid}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {createSubmitting ? 'Creating…' : 'Create customer'}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog isOpen={deleteTarget !== null} onClose={closeDeleteDialog} panelClassName="max-w-lg">
        {deleteTarget && (
          <div className="pr-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Delete customer and schema</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Permanently drops schema <code className="text-xs bg-gray-200 dark:bg-gray-700 px-1 rounded">{deriveSchemaName(deleteTarget.name)}</code> and removes <strong>{deleteTarget.name}</strong>. Cannot be undone.
            </p>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Type the company name to confirm: <span className="font-mono text-xs">{deleteTarget.name}</span>
            </label>
            <input type="text" value={deleteConfirmName} onChange={e => setDeleteConfirmName(e.target.value)}
              autoComplete="off" placeholder="Company name" disabled={deleteSubmitting}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white mb-3" />
            {deleteError && <p className="text-sm text-red-600 dark:text-red-400 mb-3">{deleteError}</p>}
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" onClick={closeDeleteDialog} disabled={deleteSubmitting}
                className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">Cancel</button>
              <button type="button" onClick={() => void submitDelete()} disabled={!confirmDeleteEnabled}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {deleteSubmitting ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
